import React, { useState, useMemo, useCallback } from 'react';
import { Container, Row, Col, Button, Card, Form, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaNetworkWired, 
  FaArrowRight, 
  FaLightbulb,
  FaRocket,
  FaFilter,
  FaSearch,
  FaTh,
  FaList,
  FaCog,
  FaExternalLinkAlt,
  FaPhone,
  FaChevronLeft,
  FaChevronRight,
  FaCloud,
  FaServer,
  FaLayerGroup,
  FaWifi
} from 'react-icons/fa';
import { useScrollAnimation } from '../../../hooks';
import { useLanguage } from '../../../contexts/LanguageContext'; // Import du hook
import { Helmet } from 'react-helmet-async';

const Switching = () => {
  const { t } = useLanguage(); // Récupération des traductions

  // Données des solutions de switching adaptées avec les traductions existantes
  const SWITCHING_DATA = useMemo(() => ({
    categories: [
      {
        id: 'campus-access',
        name: t.switching.categories.campus,
        description: t.switching.categories.campusD,
        icon: <FaWifi />
      },
      {
        id: 'top-of-rack',
        name: t.switching.categories.tor,
        description: t.switching.categories.torD,
        icon: <FaServer />
      },
      {
        id: 'spine-leaf',
        name: t.switching.categories.spine,
        description: t.switching.categories.spineD,
        icon: <FaLayerGroup />
      }
    ],

    switchProducts: [
      {
        id: 1,
        name: 'S3248T-ON',
        category: 'campus-access',
        image: '/images/switching/powerswitch-s-series-s3248t.avif',
        description: t.switching.categories.campusD,
        keyUse: 'Ideal for enterprise networks as a management switch in data center',
        options: 'OS10 / SONiC'
      },
      {
        id: 2,
        name: 'S4100-ON Series',
        category: 'top-of-rack',
        image: '/images/switching/s4148f.avif',
        description: t.switching.categories.torD,
        keyUse: 'High-performance in-rack connectivity for servers and storage',
        options: 'Multiple OS options'
      },
      {
        id: 3,
        name: 'S4300-ON Series',
        category: 'top-of-rack',
        image: '/images/switching/powerswitch-s4348f.avif',
        description: t.switching.categories.torD,
        keyUse: 'High-performance in-rack connectivity, with SONiC NOS',
        options: 'SONiC NOS'
      },
      {
        id: 4,
        name: 'S5448F-ON',
        category: 'top-of-rack',
        image: '/images/switching/s5448f-on.avif',
        description: t.switching.categories.torD,
        keyUse: 'Highest-performance in-rack connectivity for demanding data center',
        options: 'Multiple OS choices'
      },
      {
        id: 5,
        name: 'S5200-ON Series',
        category: 'top-of-rack',
        image: '/images/switching/s5448f-on.avif',
        description: t.switching.categories.torD,
        keyUse: 'Highest-performance in-rack connectivity for servers and storage',
        options: 'Multiple OS choices'
      },
      {
        id: 6,
        name: 'Z9864F-ON',
        category: 'spine-leaf',
        image: '/images/switching/z9864f-on.avif',
        description: t.switching.categories.spineD,
        keyUse: 'Highest-performance core/aggregation switching for AI fabrics',
        options: 'Advanced fabric capabilities',
        isNew: true
      },
      {
        id: 7,
        name: 'Z9664F-ON',
        category: 'spine-leaf',
        image: '/images/switching/z9864f-on.avif',
        description: t.switching.categories.spineD,
        keyUse: 'Core/aggregation switch for building highly scalable fabrics',
        options: 'Advanced fabric capabilities'
      },
      {
        id: 8,
        name: 'Z9432F-ON',
        category: 'spine-leaf',
        image: '/images/switching/z9864f-on.avif',
        description: t.switching.categories.spineD,
        keyUse: 'Highest-performance core/aggregation switching connecting racks',
        options: 'Advanced fabric capabilities'
      }
    ]
  }), [t]);

  const SWITCHING_ADVANTAGES = useMemo(() => [
    {
      id: 'open-networking',
      name: t.switching.advantages.open.name,
      description: t.switching.advantages.open.desc,
      icon: <FaNetworkWired />
    },
    {
      id: 'ai-ready',
      name: t.switching.advantages.ai.name,
      description: t.switching.advantages.ai.desc,
      icon: <FaRocket />
    },
    {
      id: 'low-latency',
      name: t.switching.advantages.latency.name,
      description: t.switching.advantages.latency.desc,
      icon: <FaLayerGroup />
    },
    {
      id: 'scalable',
      name: t.switching.advantages.scalable.name,
      description: t.switching.advantages.scalable.desc,
      icon: <FaCloud />
    }
  ], [t]);

  // États pour les filtres et la pagination
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('grid');
  
  const itemsPerPage = 9;
  
  // Hook pour les animations
  const [switchingRef, switchingInView] = useScrollAnimation();

  // Filtrage
  const filteredSwitches = useMemo(() => {
    return SWITCHING_DATA.switchProducts.filter(product => {
      const categoryMatch = selectedCategory === 'all' || product.category === selectedCategory;
      const searchMatch = searchTerm === '' || 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      return categoryMatch && searchMatch;
    });
  }, [selectedCategory, searchTerm, SWITCHING_DATA]);

  // Pagination
  const totalPages = Math.ceil(filteredSwitches.length / itemsPerPage);
  const paginatedSwitches = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredSwitches.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredSwitches, currentPage]);

  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const scrollToSolutions = () => {
    document.getElementById('solutions-section').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>

      {/* --- SEO DYNAMIQUE POUR GOOGLE --- */}
      <Helmet>
        <title>{t.switching.seo.title}</title>
        <meta name="description" content={t.switching.seo.description} />
        <link rel="canonical" href="https://www.s2i.com.tn/solutions/switching" />
        
        {/* Balises pour le partage social */}
        <meta property="og:title" content={t.switching.seo.title} />
        <meta property="og:description" content={t.switching.seo.description} />
        <meta property="og:image" content="https://www.s2i.com.tn/images/switching/powerswitch-s-series-s3248t.avif" />
      </Helmet>
      {/* ---------------------------------- */}

      {/* Hero Section */}
      <section style={{
          background: `linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(71, 85, 105, 0.9) 100%), url('/images/datacenter.webp')`,
          padding: '6rem 0 4rem 0',
          position: 'relative',
          overflow: 'hidden'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={8} className="text-center text-lg-start">
              <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
                <Badge bg="danger" className="mb-3" style={{ fontSize: '0.9rem', padding: '0.5rem 1rem', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  <FaNetworkWired className="me-2" />
                  {t.switching.hero.badge}
                </Badge>
                
                <h1 style={{ fontSize: '3.5rem', fontWeight: 700, color: '#fff', marginBottom: '1.5rem', lineHeight: '1.2' }} 
                    dangerouslySetInnerHTML={{ __html: t.switching.hero.title }} />
                
                <p style={{ fontSize: '1.3rem', color: 'rgba(255,255,255,0.9)', marginBottom: '2rem', lineHeight: '1.6', maxWidth: '600px' }}>
                  {t.switching.hero.subtitle}
                </p>
                
                <div className="d-flex flex-wrap gap-3">
                  <Button variant="danger" size="lg" onClick={scrollToSolutions} style={{ borderRadius: '16px', padding: '1rem 2.5rem', fontWeight: 600, fontSize: '1rem', border: 'none', boxShadow: '0 4px 16px rgba(217,0,0,0.4)', transition: 'all 0.3s ease' }}>
                    <FaArrowRight className="me-2" />
                    {t.switching.hero.btnExplore}
                  </Button>
                  <Button variant="outline-light" size="lg" style={{ borderRadius: '16px', padding: '1rem 2.5rem', fontWeight: 600, fontSize: '1rem', borderWidth: '2px' }}>
                    <FaLightbulb className="me-2" />
                    {t.switching.hero.btnGuide}
                  </Button>
                </div>
              </motion.div>
            </Col>
            
            <Col lg={4} className="text-center">
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }} style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '20px', padding: '2rem', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <img src="/images/switching/poweredge-pemx7000.avif" alt="PowerSwitch" style={{ width: '280px', height: '120px', objectFit: 'cover', marginBottom: '1rem', borderRadius: '16px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }} />
                <h4 style={{ color: '#fff', marginBottom: '1rem' }}>{t.switching.hero.leader}</h4>
                <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem' }}>{t.switching.hero.leaderDesc}</p>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Avantages */}
      <section className="py-5" style={{ background: '#fff' }}>
        <Container>
          <Row className="g-4">
            {SWITCHING_ADVANTAGES.map((advantage, index) => (
              <Col md={3} key={advantage.id}>
                <motion.div initial={{ opacity: 0, y: 50 }} animate={switchingInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: index * 0.1 }} whileHover={{ y: -10 }} style={{ background: '#fff', borderRadius: 16, padding: '2rem 1.5rem', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', transition: 'all 0.3s ease', border: '2px solid transparent', height: '100%' }}>
                  <div style={{ fontSize: '2.5rem', color: '#d90000', marginBottom: '1rem' }}>{advantage.icon}</div>
                  <h4 style={{ color: '#333', fontWeight: 700, marginBottom: '0.5rem' }}>{advantage.name}</h4>
                  <p style={{ color: '#666', margin: 0, fontSize: '0.95rem' }}>{advantage.description}</p>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Main Section */}
      <section id="solutions-section" className="py-5" ref={switchingRef}>
        <Container>
          <Row>
            {/* Sidebar */}
            <Col lg={3}>
              <div style={{ background: '#fff', borderRadius: '20px', padding: '1.5rem', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', position: 'sticky', top: '2rem', marginBottom: '2rem' }}>
                <div className="mb-4">
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                    <FaSearch className="me-2" /> {t.switching.sidebar.search}
                  </label>
                  <Form.Control type="text" placeholder={t.switching.sidebar.searchPlaceholder} value={searchTerm} onChange={handleSearchChange} style={{ borderRadius: '12px', border: '2px solid #e2e8f0', fontSize: '0.9rem', padding: '0.75rem 1rem' }} />
                </div>

                <div className="mb-4">
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                    <FaFilter className="me-2" /> {t.switching.sidebar.category}
                  </label>
                  <div className="d-grid gap-2">
                    <Button variant={selectedCategory === 'all' ? 'danger' : 'outline-secondary'} onClick={() => handleCategoryChange('all')} size="sm" style={{ borderRadius: '10px', fontSize: '0.8rem', padding: '0.5rem 0.75rem', textAlign: 'left' }}>
                      {t.switching.sidebar.allCats}
                    </Button>
                    {SWITCHING_DATA.categories.map(category => (
                      <Button key={category.id} variant={selectedCategory === category.id ? 'danger' : 'outline-secondary'} onClick={() => handleCategoryChange(category.id)} size="sm" style={{ borderRadius: '10px', fontSize: '0.8rem', padding: '0.5rem 0.75rem', textAlign: 'left' }}>
                        {category.icon} {category.name}
                      </Button>
                    ))}
                  </div>
                </div>

                <div style={{ padding: '1rem', background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)', borderRadius: '12px', border: '1px solid #dee2e6', marginBottom: '1.5rem' }}>
                  <p style={{ color: '#495057', margin: 0, fontWeight: 600, fontSize: '0.85rem', textAlign: 'center' }}>
                    <strong style={{ color: '#d90000' }}>{filteredSwitches.length}</strong> {t.switching.sidebar.found}
                  </p>
                  {totalPages > 1 && (
                    <p style={{ color: '#6c757d', margin: '0.25rem 0 0 0', fontSize: '0.75rem', textAlign: 'center' }}>
                      {t.contactPage.info.hours.split(' ')[0]} {currentPage} {t.switching.sidebar.pageOf} {totalPages}
                    </p>
                  )}
                </div>

                <div className="mb-3">
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>{t.switching.sidebar.view}</label>
                  <div className="btn-group d-grid" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                    <Button variant={viewMode === 'grid' ? 'danger' : 'outline-secondary'} onClick={() => handleViewModeChange('grid')} style={{ border: 'none', fontSize: '0.9rem', borderRadius: '12px 12px 0 0' }}>
                      <FaTh className="me-2" /> {t.switching.sidebar.grid}
                    </Button>
                    <Button variant={viewMode === 'list' ? 'danger' : 'outline-secondary'} onClick={() => handleViewModeChange('list')} style={{ border: 'none', fontSize: '0.9rem', borderRadius: '0 0 12px 12px' }}>
                      <FaList className="me-2" /> {t.switching.sidebar.list}
                    </Button>
                  </div>
                </div>
              </div>
            </Col>

            {/* Produits */}
            <Col lg={9}>
              {viewMode === 'grid' ? (
                <Row className="g-4">
                  {paginatedSwitches.map((product, index) => (
                    <Col lg={4} md={6} key={product.id}>
                      <motion.div initial={{ opacity: 0, y: 50 }} animate={switchingInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: index * 0.1 }} whileHover={{ y: -10 }}>
                        <Card style={{ height: '100%', border: 'none', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', background: '#fff' }}>
                          <div style={{ height: '180px', background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                            <div style={{ position: 'absolute', top: '1rem', left: '1rem' }}>
                              {product.isNew && <Badge bg="success" style={{ borderRadius: '8px', fontSize: '0.7rem' }}>{t.switching.card.new}</Badge>}
                            </div>
                            <img src={product.image} alt={product.name} style={{ width: '90%', height: '90%', objectFit: 'contain' }} />
                          </div>
                          <Card.Body style={{ padding: '1.5rem' }}>
                            <h5 style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>{product.name}</h5>
                            <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1rem', minHeight: '40px' }}>{product.description}</p>
                            <div className="mb-3">
                              <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '1rem', border: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', marginBottom: '0.5rem' }}>{t.switching.card.usage}</div>
                                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e293b' }}>{product.keyUse}</div>
                              </div>
                            </div>
                            <div>
                              <h6 style={{ fontSize: '0.8rem', fontWeight: 600, color: '#4a5568', textTransform: 'uppercase' }}>{t.switching.card.options}</h6>
                              <div style={{ fontSize: '0.8rem', color: '#64748b' }}><FaCog className="me-2" />{product.options}</div>
                            </div>
                          </Card.Body>
                        </Card>
                      </motion.div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {paginatedSwitches.map((product, index) => (
                    <motion.div key={product.id} initial={{ opacity: 0, x: -50 }} animate={switchingInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: index * 0.1 }}>
                      <Card style={{ border: 'none', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                        <Row className="g-0">
                          <Col md={3} style={{ background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img src={product.image} alt={product.name} style={{ width: '80%', objectFit: 'contain', padding: '1rem' }} />
                          </Col>
                          <Col md={9}>
                            <Card.Body>
                              <div className="d-flex justify-content-between">
                                <h5 style={{ fontWeight: 700 }}>{product.name}</h5>
                                {product.isNew && <Badge bg="success">{t.switching.card.new}</Badge>}
                              </div>
                              <p className="text-muted small">{product.description}</p>
                              <Row>
                                <Col sm={6}>
                                  <div className="small text-uppercase text-muted">{t.switching.card.usage}</div>
                                  <div className="small fw-bold">{product.keyUse}</div>
                                </Col>
                                <Col sm={6}>
                                  <div className="small text-uppercase text-muted">{t.switching.card.options}</div>
                                  <div className="small fw-bold">{product.options}</div>
                                </Col>
                              </Row>
                            </Card.Body>
                          </Col>
                        </Row>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-5">
                  <div className="d-flex gap-2">
                    <Button variant="outline-secondary" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} style={{ borderRadius: '10px' }}><FaChevronLeft /></Button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <Button key={page} variant={currentPage === page ? 'danger' : 'outline-secondary'} onClick={() => handlePageChange(page)} style={{ borderRadius: '10px', minWidth: '40px' }}>{page}</Button>
                    ))}
                    <Button variant="outline-secondary" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ borderRadius: '10px' }}><FaChevronRight /></Button>
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)', padding: '4rem 0', position: 'relative' }}>
        <Container>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={switchingInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }} className="text-center">
            <h2 style={{ fontSize: '2.5rem', fontWeight: 700, color: '#fff', marginBottom: '1rem' }}>{t.switching.cta.title}</h2>
            <p style={{ fontSize: '1.2rem', maxWidth: '700px', margin: '0 auto 2.5rem auto', color: 'rgba(255,255,255,0.9)' }}>{t.switching.cta.subtitle}</p>
            <div className="d-flex flex-wrap justify-content-center gap-3">
              <Button variant="light" size="lg" style={{ borderRadius: '16px', padding: '1rem 2.5rem', fontWeight: 600, color: '#1e293b', border: 'none' }}>
                <FaPhone className="me-2" /> {t.switching.cta.btnExpert}
              </Button>
              <Button variant="outline-light" size="lg" style={{ borderRadius: '16px', padding: '1rem 2.5rem', fontWeight: 600, borderWidth: '2px' }}>
                <FaExternalLinkAlt className="me-2" /> {t.switching.cta.btnGuide}
              </Button>
            </div>
          </motion.div>
        </Container>
      </section>
    </div>
  );
};

export default Switching;